<template>
<TheStdLayout>

	<template #pageheader>{{ $tc('School', 2) }}</template>

	<div class="px-4 py-6 mx-auto sm:px-6 md:px-8">

		<form @submit.prevent="handleSearchQuery" class="flex flex-wrap items-center justify-start w-full my-2 gap-3">
            <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex pt-2 md:pt-0 md:items-center pointer-events-none">
                    <MagnifyingGlassIcon class="w-5 h-5 text-base-content-light" />
                </div>
                <div class="flex flex-wrap -mx-3">
                    <div class="w-full px-3 md:mb-0">
                        <sms-input title="Search by name, hubspot id or owner name." placeholder="Name / hubspot id / owner" v-model="filter.query" name="search" minlength="3" type="search" class="pl-10 w-full md:w-56" />
                    </div>
                </div>
            </div>
            <div class="shrink-0">
                <sms-select v-model="filter.subscriptionPlan" name="plan" label="" class="capitalize w-40" :options="[
					{
						value: null,
						label: $t('All plans')
					},

					...subscriptionPlans.map((type) => {
						return {
							value: type.key,
							label: $t(type.label)
						};
					}),
				]">
                </sms-select>
            </div>

			<div class="shrink-0 w-32">
				<sms-select v-model="filter.subscriptionType" name="plan" label="" class="capitalize" :options="[
					{
						value: null,
						label: $t('All types')
					},

					...subscriptionTypes.map((type) => {
						return {
							value: type.key,
							label: $t(type.label)
						};
					}),
				]">
				</sms-select>
			</div>

			<div class="shrink-0 w-36">
				<sms-select v-model="filter.country" name="country" label="" class="capitalize" :options="[
					{
						value: null,
						label: $t('All countries')
					},
					...countries
				]">
				</sms-select>
			</div>

			<div class="shrink-0">
				<sms-select v-model="filter.expired" name="expired" label="" class="capitalize w-40" :options="[
					{
						value: null,
						label: $t('Any status')
					},
					{
						value: true,
						label: $t('Expired')
					},
					{
						value: false,
						label: $t('Active')
					}
				]">
				</sms-select>
			</div>

			<div class="shrink-0">
				<sms-select v-model="filter.curriculum" name="curriculum" label="" class="capitalize w-40" :options="curriculumOptions">
				</sms-select>
			</div>

			<div class="shrink-0 w-40">
				<sms-select v-model="filter.schoolGroupId" name="school_group" label="" class="" :options="schoolGroupOptions">
				</sms-select>
			</div>

            <button type="submit" class="btn btn-primary">
							<span v-if="filtering" class="loading"></span>
							{{ $t('Search') }}
						</button>
            <button v-show="filterApplied" @click="handleFilterReset" type="button" class="button">{{ $t('Reset') }}</button>
        </form>

		<Dropdown class="top-16" wrapperClasses="sticky top-2">
			<template #trigger>
				<FloatingPlusButton class="ml-4" />
			</template>
			<template #content>
				<DropdownMenu class="w-56">
					<DropdownMenuItem>
						<a @click="handleOrgCreateRequest">
							<BuildingOfficeIcon class="w-5 h-5 mr-2" />
							{{ $t('Add school') }}
						</a>
					</DropdownMenuItem>
					<DropdownMenuItem>
						<a @click="handleGroupCreateRequest">
							<BuildingOffice2Icon class="w-5 h-5 mr-2" />
							{{ $t('Add school group') }}
						</a>
					</DropdownMenuItem>
				</DropdownMenu>
			</template>
		</Dropdown>

		<table class="divide-y divide-base-200 w-full mb-8">

			<thead>
				<tr>
					<th scope="col" class="table-col-title">
						<SortableHeader :order="organisationsStore.order" property="name" @change="organisationsStore.changeOrder">{{ $t('Name') }}</SortableHeader>
					</th>
					<th scope="col" class="hidden md:table-cell table-col-title">
						{{ $t('Subscription plan') }}
					</th>
					<th scope="col" class="hidden md:table-cell table-col-title">
						<SortableHeader :order="organisationsStore.order" property="hubspot_id" @change="organisationsStore.changeOrder">{{ $t('Hubspot id') }}</SortableHeader>
					</th>
					<th scope="col" class="hidden md:table-cell table-col-title">
						<SortableHeader :order="organisationsStore.order" property="country" @change="organisationsStore.changeOrder">{{ $t('Country') }}</SortableHeader>
					</th>
					<th scope="col" class="hidden md:table-cell table-col-title">
						{{ $t('Curriculum') }}
					</th>
					<th scope="col" class="hidden md:table-cell table-col-title">
						{{ $t('School group') }}
					</th>
					<th scope="col" class="hidden md:table-cell table-col-title">
						<SortableHeader :order="organisationsStore.order" property="created_at" @change="organisationsStore.changeOrder">{{ $t('Created at') }}</SortableHeader>
					</th>
					<th scope="col" class="hidden md:table-cell table-col-title">
						<SortableHeader :order="organisationsStore.order" property="termination_date" @change="organisationsStore.changeOrder">{{ $t('Termination') }}</SortableHeader>
					</th>
					<th scope="col" class="px-6 py-3">
						<span class="sr-only">{{$t('Actions')}}</span>
					</th>
				</tr>
			</thead>

			<tbody class="bg-base divide-y divide-base-200">

				<tr v-show="!loaded">
					<td colspan="9">
						<Skeleton content-type="userlist" :items="3" class="m-4" thumbnail-classes="mask mask-squircle" />
					</td>
				</tr>

				<tr v-for="(org, key) in organisationsStore.organisations" :key="key">
    				<td class="px-4 py-3 whitespace-nowrap print:py-1">
						<span class="flex gap-1">
							<svg v-if="org.type === 'group'" xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 256 256">
								<path d="M120,80A40,40,0,1,1,80,40,40,40,0,0,1,120,80Zm56,40a40,40,0,1,0-40-40A40,40,0,0,0,176,120ZM80,136a40,40,0,1,0,40,40A40,40,0,0,0,80,136Zm96,0a40,40,0,1,0,40,40A40,40,0,0,0,176,136Z"></path>
							</svg>
							{{ org.name }}
						</span>
						<div v-if="org.type === 'group'" class="text-xs text-base-content-light text-nowrap">
							{{ $t('School group') }}
						</div>
						<div v-else-if="org.owner" class="text-xs text-base-content-light">
							{{ $t('Owned by') }}

							<button v-if="org.owner.id === this.usersStore.me.id" @click="switchToOrganisation(org)" type="button" class="hover:text-accent inline-flex gap-1 items-center" title="Impersonate">
								<span class="font-bold">{{ $t('You') }}</span>
								<ArrowTopRightOnSquareIcon class="w-4 h-4 inline-block" />
							</button>

							<button v-if="org.owner.id !== this.usersStore.me.id" @click="impersonate(org.owner.id, org.id)" type="button" class="hover:text-accent inline-flex gap-1 items-center" title="Impersonate">
								<span class="font-bold">{{  org.owner.getNameIdentifier() }}</span>
								<ArrowTopRightOnSquareIcon class="w-4 h-4 inline-block" />
							</button>
						</div>
						<div v-else class="text-sm text-danger">
							{{ $t('Warning') }} - {{ $t('No owner') }}!
						</div>
					</td>
					<td class="hidden md:table-cell table-col-title">
						<span v-if="org.type !== 'group'" class="badge border-none text-[0.6rem] text-nowrap" :class="{
							'bg-green-400': org.isPaying(),
							'bg-orange-300': org.subscription_type === 'trial'
						}">
							{{ $t(displaySubscriptionPlan(org.subscription_plan)) }}
							{{ org.subscription_type ? $t(displaySubscriptionType(org.subscription_type)) : '' }}
						</span>
					</td>
					<td class="hidden md:table-cell px-2 lg:px-6 py-2 whitespace-nowrap text-sm">
						{{ org.hubspot_id }}
					</td>
					<td class="hidden md:table-cell px-2 lg:px-6 py-2 whitespace-nowrap text-sm">
						{{ org.country }}
					</td>
					<td class="hidden md:table-cell px-2 lg:px-6 py-2 whitespace-nowrap text-sm">
						{{ org.curriculum?.name }}
					</td>
					<td class="hidden md:table-cell px-2 lg:px-6 py-2 whitespace-nowrap text-sm">
						{{ org.schoolGroup?.name }}
					</td>
					<td class="hidden lg:table-cell px-2 lg:px-6 py-2 whitespace-nowrap text-sm">
						{{ localDate(org.created_at) }}
					</td>
					<td class="hidden lg:table-cell px-2 lg:px-6 py-2 whitespace-nowrap text-sm">
						<template v-if="org.termination_date">
							{{ org.termination_date.toLocaleString() }}
						</template>
					</td>
    				<td class="px-4 py-3 whitespace-nowrap text-right text-sm font-medium flex justify-end gap-2 sm:gap-4 print:py-1">
      					<div class="flex-row print:hidden">
							<button class="btn btn-ghost btn-circle bg-base-100" @click="handleOrgEditRequest(org)">
								<PencilIcon class="w-5 h-5"></PencilIcon>
							</button>
						</div>
					</td>

				</tr>

			</tbody>
		</table>

		<Pagination v-if="organisationsStore.currentPage" :page="organisationsStore.currentPage" @requestPage="loadPage"  />
  	</div>
	<OrganisationEditor  v-if="showOrgFormEditor" :organisation="organisation" :type="newOrganisationType" @requestClose="closeEditor"/>
</TheStdLayout>
</template>


<script lang="ts">

import { mapActions, mapStores } from "pinia";
import {useOrganisationsStore} from "@/stores/Organisations.store";

import Skeleton from '@/components/ui/Skeleton.vue';
import Pagination from "./ui/Pagination.vue";
import { ArrowTopRightOnSquareIcon, BuildingOffice2Icon, BuildingOfficeIcon, MagnifyingGlassIcon, PencilIcon } from '@heroicons/vue/24/outline';
import { useUsersStore } from "@/stores/Users.store";
import { PaginationState } from "@/models/PaginationState.model";
import { DateTime } from "luxon";
import SortableHeader from "@/components/ui/SortableHeader.vue";
import {SUPPORTED_COUNTRIES} from "@/i18n.ts";
import {SUBSCRIPTION_TYPES, SUBSCRIPTION_PLANS} from "@/config/organisations.config";
import { OrganisationFilterDef } from "@/stores/filters/OrganisationFilterDef";
import OrganisationEditor from "@/components/OrganisationEditor.vue";
import Dropdown from '@/components/ui/Dropdown.vue';
import DropdownMenu from '@/components/ui/DropdownMenu.vue';
import DropdownMenuItem from '@/components/ui/DropdownMenuItem.vue';
import { getCurriculumRegistry } from "@/config/curricula.registry";
import { Organisation } from "@/models/Organisation.model";

export default {

	data() {
		return {
			loaded: false,
			organisation: null,
			showOrgFormEditor: false,
			newOrganisationType: 'school' as 'school' | 'group',
			schoolGroups: [] as Organisation[],
			filter: null as OrganisationFilterDef | null,
			filtering: false,
			filterApplied: false,
			currentResultsPage: 1,
		};
	},

	components: {
		SortableHeader,
		Skeleton,
		Pagination,
		PencilIcon,
		ArrowTopRightOnSquareIcon,
		BuildingOfficeIcon,
		BuildingOffice2Icon,
		MagnifyingGlassIcon,
		OrganisationEditor,
		Dropdown,
		DropdownMenu,
		DropdownMenuItem,
	},

	computed: {

		...mapStores(useOrganisationsStore, useUsersStore),

		localDate() {
			return (date: Date) => {
				return DateTime.fromJSDate(date).toLocaleString();
			};
		},

		countries() {
			let countries = [];
			for (const [key, value] of Object.entries(SUPPORTED_COUNTRIES)) {
				countries.push({
					value: key,
					label: value,
				});
			}
			return countries;
		},

		curriculumOptions() {
			return [
				{ value: null, label: this.$t('All curricula') },
				...getCurriculumRegistry().map((entry) => ({ value: entry.id, label: entry.name })),
			];
		},

		schoolGroupOptions() {
			return [
				{ value: null, label: this.$t('All school groups') },
				...this.schoolGroups.map((group) => ({ value: group.id, label: group.name })),
			];
		},

		subscriptionTypes() {
			return SUBSCRIPTION_TYPES;
		},

		subscriptionPlans() {
			return SUBSCRIPTION_PLANS;
		},

		displaySubscriptionPlan() {
			return (plan: string) => {
				for (let i = 0; i < SUBSCRIPTION_PLANS.length; i ++) {
					if (SUBSCRIPTION_PLANS[i].key === plan) {
						return SUBSCRIPTION_PLANS[i].label;
					}
				}
				return plan;
			};
		},

		displaySubscriptionType() {
			return (plan: string) => {
				for (let i = 0; i < SUBSCRIPTION_TYPES.length; i ++) {
					if (SUBSCRIPTION_TYPES[i].key === plan) {
						return SUBSCRIPTION_TYPES[i].label;
					}
				}
				return plan;
			};
		}
	},

	beforeMount() {
		this.initializeFilter();
	},

	mounted() {

		// Check if we have access
		if (!this.usersStore.me.isSuperAdmin()) {
			this.$router.push({ name: 'user.list' });
			return;
		}

		this.loadOrganisations();
		this.loadSchoolGroups();
	},

	methods: {

		...mapActions(useUsersStore, ['impersonate']),
		...mapActions(useOrganisationsStore, ['switchToOrganisation']),

		handleOrgCreateRequest() {
			this.newOrganisationType = 'school';
			this.showOrgFormEditor = true;
		},

		handleGroupCreateRequest() {
			this.newOrganisationType = 'group';
			this.showOrgFormEditor = true;
		},

		handleOrgEditRequest(org) {
			this.organisation = org;
			this.showOrgFormEditor = true;
		},

		async loadOrganisations() {
			await this.organisationsStore.load(null, this.filter);
			this.loaded = true;
		},

		async loadSchoolGroups() {
			this.schoolGroups = await this.organisationsStore.loadGroupOrganisations();
		},

		closeEditor() {
			this.organisation = null;
			this.showOrgFormEditor = false;

			// A school group may have been added, renamed or removed.
			this.loadSchoolGroups();
		},

		initializeFilter() {
			this.filter = new OrganisationFilterDef();
		},

		async handleSearchQuery() {
			this.filterApplied = true;
			this.filtering = true;
			await this.loadOrganisations();
			this.filtering = false;
		},

		handleFilterReset() {
			this.initializeFilter();
			this.filterApplied = false;
			this.loadOrganisations();
		},

		loadPage(page: PaginationState) {
			this.organisationsStore.load(page, this.filter);
		},
	},

};

</script>
